import type { GetWorkflowsByProgramResponse } from '@compass/client/types/workflow';
import { workflowService } from '@services/api';
import { useStore } from '@store/index';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useStableFold } from '@/hooks/internal/useStableFold';

/**
 * Isolated cache key for the My Cases workflows fetch. Kept distinct from
 * `queryKeys.workflows.byProgram(programId)` so this hook does not populate
 * the shared workflows cache that `useWorkflowsByProgram` reads from — see
 * `useWorkflowSlaMap` doc-comment below for the full rationale.
 */
export const mycasesWorkflowsQueryKey = (orgId: string | null | undefined, programId: string) =>
  ['compass', 'mycases', 'workflows', orgId, programId] as const;

/**
 * Pure fold extracted from the hook: walks a list of "workflows-by-program"
 * query results and merges them into a single `workflow_id → sla.minutes` map.
 * Workflows without an `id` or without a numeric `sla.minutes` are skipped.
 */
export const foldWorkflowSlaMap = (
  results: Array<{ data?: Pick<GetWorkflowsByProgramResponse, 'workflows'> }>,
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const q of results) {
    for (const w of q.data?.workflows ?? []) {
      if (w.id && typeof w.sla?.minutes === 'number') {
        map.set(w.id, w.sla.minutes);
      }
    }
  }
  return map;
};

/**
 * Pure fold mirroring `foldWorkflowSlaMap` but keyed `workflow_id → version`.
 * Used by the SLA timer column to detect v3/v4 workflows in cross-program views,
 * where no single `selectedWorkflow` is in scope. Workflows without an `id` or
 * a non-empty `version` are skipped.
 */
export const foldWorkflowVersionMap = (
  results: Array<{ data?: Pick<GetWorkflowsByProgramResponse, 'workflows'> }>,
): Map<string, string> => {
  const map = new Map<string, string>();
  for (const q of results) {
    for (const w of q.data?.workflows ?? []) {
      if (w.id && w.version) {
        map.set(w.id, w.version);
      }
    }
  }
  return map;
};

/**
 * Builds a workflow-id → SLA-minutes map for the given set of programs.
 *
 * Cross-program views (e.g. My Cases) don't have a single `selectedWorkflow` in
 * scope, so the SLATimer column can't read `selectedWorkflow.sla.minutes`. This
 * hook fetches the workflows for each program in parallel and flattens them
 * into a single map keyed by workflow ID.
 *
 * IMPORTANT — query key isolation: this hook uses its own cache namespace
 * (`['compass', 'mycases', 'workflows', programId]`) rather than the shared
 * `queryKeys.workflows.byProgram(programId)` key used by `useWorkflowsByProgram`.
 *
 * That's deliberate: `useWorkflowsByProgram` has a Zustand side effect that
 * fires whenever its query data appears (including via cache hit), calling
 * `setWorkflows` and auto-selecting `selectedWorkflowId`. If we shared the
 * key, our pre-population would make that side effect fire synchronously when
 * CaseDetailsRouter mounts after a My Cases → case-detail navigation — picking
 * an arbitrary "first" workflow before `caseDetails.workflow_id` is known. That
 * temporarily swaps the case detail PageComponent (Default ↔ Knipper),
 * triggering a remount that re-runs `useCase(refetchOnMount: 'always')` and
 * aborts the open `watch` SSE. Separate cache → no pre-population, no remount.
 */
export const useWorkflowSlaMap = (programIds: (string | undefined)[]) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const uniqueProgramIds = useMemo(
    () => Array.from(new Set(programIds.filter((id): id is string => !!id))),
    [programIds],
  );

  const queries = useQueries({
    queries: uniqueProgramIds.map(programId => ({
      queryKey: mycasesWorkflowsQueryKey(selectedOrganizationId, programId),
      queryFn: () =>
        workflowService.getWorkflowsByProgram({
          organization_id: selectedOrganizationId!,
          program_id: programId,
        }),
      enabled: !!selectedOrganizationId && !!programId,
      // Workflows rarely change during a session.
      staleTime: 10 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    })),
  });

  // `useQueries` returns a fresh array on every render; `useStableFold`
  // fingerprints results by `dataUpdatedAt` + length so the resulting Map
  // identity stays stable until actual workflow data changes.
  const slaByWorkflowId = useStableFold(
    queries,
    q => q.data?.workflows?.length ?? 0,
    foldWorkflowSlaMap,
  );

  const versionByWorkflowId = useStableFold(
    queries,
    q => q.data?.workflows?.length ?? 0,
    foldWorkflowVersionMap,
  );

  return {
    slaByWorkflowId,
    versionByWorkflowId,
    isLoading: queries.some(q => q.isLoading),
  };
};
